import type { ReactNode } from 'react'

type AssistQuoteCardProps = {
  /**
   * The quoted turn, usually an `<AssistQuoteLine>`. Omitted when the panel hides
   * turn context or the group has no quote, which leaves the card as a plain box
   * around its children.
   */
  header?: ReactNode
  /** Trailing affordance on the header row; a quote group puts its chevron here. */
  adornment?: ReactNode
  children?: ReactNode
  expanded?: boolean
  onToggle?: () => void
  contentId?: string
}

/**
 * The tinted shell around one quoted speaker turn and whatever that turn gave
 * rise to. Every quote group inside a suggestion card reads as the same kind of
 * thing, and the turn it is headed by stays outside the collapsible body.
 */
export default function AssistQuoteCard({
  header,
  adornment,
  children,
  expanded = true,
  onToggle,
  contentId,
}: AssistQuoteCardProps) {
  const hasHeaderRow = Boolean(header) || Boolean(adornment)
  const isInteractive = Boolean(onToggle)

  const headerContent = (
    <>
      {header}
      {adornment}
    </>
  )

  return (
    <div
      className="rounded-lg border border-[#d1fae5] bg-[rgba(236,253,245,0.5)] p-[11px] dark:border-emerald-500/30 dark:bg-emerald-500/10"
      data-testid="copilot-quote-card"
    >
      {hasHeaderRow ? (
        // `gap-[15px]` + `pr-[5px]` are the design's quoted-turn-to-chevron
        // spacing. Both sit off Tailwind's scale on purpose: Figma says 15px/5px.
        isInteractive ? (
          <button
            type="button"
            onClick={onToggle}
            aria-expanded={expanded}
            aria-controls={contentId}
            className="flex w-full items-center gap-[15px] pr-[5px] text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2"
          >
            {headerContent}
          </button>
        ) : (
          <div className="flex items-center gap-[15px] pr-[5px]">{headerContent}</div>
        )
      ) : null}
      {/* `mt-5` is the design's turn-to-suggestions gap. Without a header row
          there is nothing to space away from, so the box keeps only its padding
          rather than opening a gap above the first chip. */}
      {children && expanded ? (
        <div id={contentId} className={hasHeaderRow ? 'mt-5' : undefined}>
          {children}
        </div>
      ) : null}
    </div>
  )
}
